import { createSlice, PayloadAction, createAsyncThunk, nanoid } from '@reduxjs/toolkit';
import { SpeechRateAnalysis } from '../services/speechRateService';
import { speechRateApi } from '../services/apiService';
import { TRANSCRIPT_STORE_LIMIT } from '../constants/sessionHistory';
import { isSameQuestionCard, isSameTranscriptLine, mergeByTime, withoutDuplicates } from '../utils/sessionHistory';
import { mergeTranscriptLines, moveQuestionCards, moveTranscriptLines } from '../utils/participantLinesMoved';
import { participantLinesMoved, participantMerged, participantReclassified } from './participantActions';
import {
  applyRoleChangeToCards,
  decisionAfter,
  isSalespersonNow,
  ParticipantDecision,
  ParticipantRoleChange,
  relabelTranscripts,
  shouldApplyRoleChange,
} from '../utils/participantRoleChange';
import { CardKey, describeCardKey, isSameCard } from '../utils/cardAnswerPairing';

// Enhanced deduplication function that catches more types of duplicates
function isDuplicateTranscript(newText: string, existingText: string, timeDiff: number): boolean {
  const normalize = (text: string) => text.toLowerCase().trim().replace(/\s+/g, ' ');
  const normalizedNew = normalize(newText);
  const normalizedExisting = normalize(existingText);
  
  // Check for exact duplicates — same time-bound guard as the partial-match
  // branch below. Without it, a rep legitimately repeating the same sentence
  // (e.g. re-answering a pushback) gets silently dropped from the transcript
  // forever, no matter how far apart in time the two turns actually were.
  if (normalizedNew === normalizedExisting) {
    return timeDiff < 3000;
  }
  
  // Check for partial duplicates (one is contained within the other)
  if (normalizedNew.includes(normalizedExisting) || normalizedExisting.includes(normalizedNew)) {
    // Only consider it a duplicate if the time difference is small (within 3 seconds)
    return timeDiff < 3000;
  }
  
  // Check for very similar texts using a simple similarity check
  const words1 = normalizedNew.split(' ').filter(word => word.length > 2);
  const words2 = normalizedExisting.split(' ').filter(word => word.length > 2);
  
  if (words1.length > 0 && words2.length > 0) {
    const commonWords = words1.filter(word => words2.includes(word));
    const similarity = commonWords.length / Math.max(words1.length, words2.length);
    
    // If similarity is high (>80%) and time difference is small, consider it a duplicate
    if (similarity > 0.8 && timeDiff < 5000) {
      return true;
    }
  }
  
  return false;
}

export interface EntryFeedback {
  rating: 'up' | 'down';
  reason?: string;
  submittedAt: string;
}

export interface ConversationEntry {
  id: string;
  question: string;
  answer?: string;
  stored_answer?: string;  // Answer from vector store database
  category: string;
  relevance_score: number;
  timestamp: string;
  status: 'pending' | 'completed' | 'error';
  error?: string;
  isNew?: boolean;
  feedback?: EntryFeedback;
  /** Loaded from the saved session (history restore): shown silently, marked as earlier. */
  isRestored?: boolean;
  /** Who raised it (the frame's participantId / speakerName); lines moved to a newcomer take their cards along. */
  participantId?: string;
  speakerName?: string;
  /**
   * The ids its answers are paired on (utils/cardAnswerPairing), as the
   * question frame carried them. Never rewritten: a line move changes
   * `participantId` above, not the key the server's answers name.
   */
  cardKey?: CardKey;
}

export interface TranscriptEntry {
  id: string;
  text: string;
  speaker: 'customer' | 'salesperson';
  // Individual participant identity (multi-party attribution).
  participantId?: string;
  speakerName?: string;
  timestamp: string;
  /** Loaded from the saved session (history restore): shown muted, never flashes the bar. */
  isRestored?: boolean;
}

export interface Participant {
  participantId: string;
  name?: string;
  role: 'customer' | 'salesperson';
  source?: string;
  externalId?: string;
  /**
   * Bot mode: the participant's email (Recall, or the calendar invite). A
   * signed-in user whose email matches a salesperson row IS that participant
   * on their own device (alert labels, speech rate; 2026-10-05).
   */
  email?: string;
  /** A person confirmed this row (the bot owner's roster confirm, on any device). */
  confirmed?: boolean;
  /** When that confirm was made (services epoch ms): the newest confirm wins (utils/participantRoleChange). */
  confirmedAt?: number;
}

export interface ParticipantAssignment {
  participantId: string;
  role: 'customer' | 'salesperson';
  name?: string;
}

/** One changed participant from the end-of-call review (only changed fields). */
export interface ParticipantEditPayload {
  participantId: string;
  role?: 'customer' | 'salesperson';
  name?: string;
}

export interface SpeechRateAlert {
  words_per_minute: number;
  /** The WPM the rep was judged against, so the alert can say what it means. */
  wpm_threshold: number;
  timestamp: string;
  /**
   * The rep who spoke too fast (services 2026-10-05: per participant on a
   * desktop call, per speaker in bot mode). The banner shows only the viewer's
   * own; absent from an older service.
   */
  participantId?: string;
}

// Async thunk for fetching speech rate settings (read-only from frontend)
export const fetchSpeechRateSettings = createAsyncThunk(
  'conversation/fetchSpeechRateSettings',
  async () => {
    const response = await speechRateApi.getSettings();
    return response.data;
  }
);

interface ConversationState {
  entries: ConversationEntry[];
  transcripts: TranscriptEntry[];
  speechRateSettings: {
    enabled: boolean;
    wpm_threshold: number;
  };
  currentSpeechRate: {
    /** Null until there is enough speech to divide by — never a fake 0. */
    words_per_minute: number | null;
    is_too_fast: boolean;
  };
  lastSpeechRateAlert?: SpeechRateAlert;
  stats: {
    questionsDetected: number;
    pendingAnswers: number;
  };
  isSalespersonHost: boolean;  // Track if salesperson is the host or participant
  participants: Participant[];  // Multi-party roster (1+ salespeople + N customers)
  salespersonResolved: boolean; // false → prompt "which participant is you?"
  confirmedParticipantIds: string[]; // participantIds the creator confirmed or acknowledged
  // The line being spoken right now, word by word, before it is committed to
  // `transcripts`. Only training sets it (gpt-live streams its transcript as it
  // speaks); a live call's Deepgram lines arrive finished, so it stays null.
  interimTranscript: InterimTranscript | null;
  /** Shown when the server refused this user's roster edit (only the bot's owner may make one). */
  rosterEditNotice: string | null;
  /** `participant_lines_moved` entries already applied, so a repeated message moves nothing. */
  movedLineKeys: string[];
  /**
   * The newest HUMAN decision per participantId (role, name, confirmedAt):
   * the owner's roster confirm on this app or any device, or a standalone
   * relabel. A roster snapshot never reverts one (utils/participantRoleChange R3).
   */
  participantDecisions: Record<string, ParticipantDecision>;
}

export interface InterimTranscript {
  speaker: 'customer' | 'salesperson';
  text: string;
}

const initialState: ConversationState = {
  entries: [],
  transcripts: [],
  speechRateSettings: {
    // 190 WPM, matching the server default. The old default here was
    // `time_window: 10, max_words: 20` — 120 WPM — which is ORDINARY
    // conversational speed, so every rep tripped it during the seconds before
    // the real settings arrived from the API.
    enabled: true,
    wpm_threshold: 190,
  },
  currentSpeechRate: {
    words_per_minute: null,
    is_too_fast: false,
  },
  stats: {
    questionsDetected: 0,
    pendingAnswers: 0,
  },
  isSalespersonHost: true,  // Default: salesperson is the host
  participants: [],
  salespersonResolved: true,
  confirmedParticipantIds: [],
  interimTranscript: null,
  rosterEditNotice: null,
  movedLineKeys: [],
  participantDecisions: {},
};

/**
 * The card an answer frame fills (utils/cardAnswerPairing). A keyed frame
 * fills the pending card with exactly its key, or nothing: with no such card
 * it is logged and dropped, never given to another card. A frame without a
 * key (an old server) keeps the old rule: the first pending card with the
 * same question.
 */
function cardForAnswer(
  entries: ConversationEntry[],
  question: string,
  cardKey: CardKey | null | undefined,
  frame: string
): ConversationEntry | undefined {
  if (!cardKey) return entries.find((entry) => entry.question === question && entry.status === 'pending');
  const sameCard = entries.filter((entry) => entry.cardKey && isSameCard(entry.cardKey, cardKey));
  const pending = sameCard.find((entry) => entry.status === 'pending');
  if (!pending) {
    const reason = sameCard.length > 0 ? 'its card is no longer waiting for one' : 'no card has its key';
    console.warn(`[conversation] ${frame} dropped, ${reason}: ${describeCardKey(cardKey)}`);
  }
  return pending;
}

const isPendingCard = (entry: ConversationEntry): boolean => entry.status === 'pending';

/**
 * R2 (utils/participantRoleChange): a human decision about one participant,
 * applied to everything this app shows. Ignored when older than the decision
 * held. The landmine and competitor slices relabel their alerts on the same
 * action; nothing is ever re-detected.
 */
function applyRoleChange(state: ConversationState, change: ParticipantRoleChange): void {
  const { participantId } = change;
  const held = state.participantDecisions[participantId];
  if (!shouldApplyRoleChange(held, change)) return;
  state.participantDecisions[participantId] = decisionAfter(held, change);

  const row = state.participants.find((p) => p.participantId === participantId);
  if (row) {
    if (change.role) row.role = change.role;
    if (change.name) row.name = change.name;
    row.confirmed = true;
    if (change.confirmedAt !== undefined) row.confirmedAt = change.confirmedAt;
  } else {
    state.participants.push({
      participantId,
      role: change.role ?? 'customer',
      name: change.name,
      confirmed: true,
      ...(change.confirmedAt !== undefined ? { confirmedAt: change.confirmedAt } : {}),
    });
  }

  state.transcripts = relabelTranscripts(state.transcripts, change);
  const cards = applyRoleChangeToCards(state.entries, change);
  state.entries = cards.entries;
  state.stats.questionsDetected = Math.max(0, state.stats.questionsDetected - cards.removed.length);
  state.stats.pendingAnswers = Math.max(0, state.stats.pendingAnswers - cards.removed.filter(isPendingCard).length);

  // Decided (here or on another device): the roles popup never re-opens for them.
  if (!state.confirmedParticipantIds.includes(participantId)) state.confirmedParticipantIds.push(participantId);
  state.salespersonResolved = state.participants.some((p) => p.role === 'salesperson');
}

/**
 * R3: upsert roster entries (participants_update, salesperson_unresolved, the
 * history restore). A decided participant keeps the decided role and name,
 * whatever the snapshot says (a stale Recall roster, another task's re-sync);
 * every line of each entry is relabelled to its effective identity, as the
 * desktops' `applyParticipantsUpdate` does. Mongo persists recall ids as JSON
 * numbers and live events carry strings: one row per id either way.
 */
function mergeRosterEntries(state: ConversationState, incoming: Participant[]): void {
  const byId = new Map(state.participants.map((p) => [p.participantId, p]));
  for (const p of incoming || []) {
    if (!p?.participantId) continue;
    const pid = String(p.participantId);
    const existing = byId.get(pid);
    const decision = state.participantDecisions[pid];
    const merged: Participant = { ...existing, ...p, participantId: pid };
    if (decision) {
      merged.role = decision.role ?? existing?.role ?? p.role;
      merged.name = decision.name ?? existing?.name ?? p.name;
    }
    byId.set(pid, merged);
    state.transcripts = relabelTranscripts(state.transcripts, {
      participantId: pid,
      role: merged.role,
      ...(merged.name ? { name: merged.name } : {}),
    });
  }
  state.participants = Array.from(byId.values());
}

const conversationSlice = createSlice({
  name: 'conversation',
  initialState,
  reducers: {
    addQuestion: (state, action: PayloadAction<{
      question: string;
      category: string;
      relevance_score: number;
      timestamp: string;
      participantId?: string;
      speakerName?: string;
      cardKey?: CardKey;
    }>) => {
      // R4: a card raised by someone this app now knows as a salesperson was in
      // flight when they were reclassified. A salesperson never keeps a
      // customer concern (the saver refuses it too).
      if (isSalespersonNow(action.payload.participantId, state.participantDecisions, state.participants)) return;
      // Already shown as history (the saved session reached this client before
      // the live frame did): one card, and the restored one stays silent. It
      // takes the live frame's key, which is what this card's answers will name.
      const restored = state.entries.find((entry) => entry.isRestored && isSameQuestionCard(entry, action.payload));
      if (restored) {
        if (action.payload.cardKey) restored.cardKey = action.payload.cardKey;
        return;
      }
      // Unique even for the same question twice in one millisecond: edits,
      // feedback and React keys find a card by this id.
      const id = `${action.payload.question}_${nanoid()}`;
      const entry: ConversationEntry = {
        id,
        ...action.payload,
        status: 'pending',
        isNew: true,
      };
      state.entries.push(entry);
      state.stats.questionsDetected += 1;
      state.stats.pendingAnswers += 1;
    },
    
    markEntryAsOld: (state, action: PayloadAction<string>) => {
      const entry = state.entries.find(e => e.id === action.payload);
      if (entry) {
        entry.isNew = false;
      }
    },
    
    addStoredAnswer: (state, action: PayloadAction<{
      question: string;
      stored_answer: string;
      timestamp: string;
      cardKey?: CardKey | null;
    }>) => {
      const entry = cardForAnswer(state.entries, action.payload.question, action.payload.cardKey, 'stored_answer_generated');
      if (entry) {
        entry.stored_answer = action.payload.stored_answer;
        // Don't change status yet - wait for AI answer
      }
    },

    addAnswer: (state, action: PayloadAction<{
      question: string;
      answer: string;
      timestamp: string;
      cardKey?: CardKey | null;
    }>) => {
      const entry = cardForAnswer(state.entries, action.payload.question, action.payload.cardKey, 'answer_generated');
      if (entry) {
        entry.answer = action.payload.answer;
        entry.status = 'completed';
        state.stats.pendingAnswers -= 1;
      }
    },
    
    markAnswerError: (state, action: PayloadAction<{
      question: string;
      error: string;
      timestamp: string;
      cardKey?: CardKey | null;
    }>) => {
      const entry = cardForAnswer(state.entries, action.payload.question, action.payload.cardKey, 'answer_error');
      if (entry) {
        entry.error = action.payload.error;
        entry.status = 'error';
        state.stats.pendingAnswers -= 1;
      }
    },
    
    
    updateCurrentSpeechRate: (state, action: PayloadAction<{
      words_per_minute: number;
      is_too_fast: boolean;
    }>) => {
      state.currentSpeechRate = action.payload;
    },
    
    updateSpeechRateAnalysis: (state, action: PayloadAction<SpeechRateAnalysis>) => {
      state.currentSpeechRate = {
        words_per_minute: action.payload.words_per_minute,
        is_too_fast: action.payload.is_too_fast,
      };
      
      // Create alert if speaking too fast
      // `is_too_fast` already honours the two-minute refractory, so this only
      // fires for an alert the rep is actually meant to see.
      if (action.payload.is_too_fast && action.payload.words_per_minute !== null) {
        state.lastSpeechRateAlert = {
          words_per_minute: action.payload.words_per_minute,
          wpm_threshold: action.payload.wpm_threshold,
          timestamp: new Date().toISOString(),
        };
      }
    },
    
    addSpeechRateAlert: (state, action: PayloadAction<SpeechRateAlert>) => {
      state.lastSpeechRateAlert = action.payload;
      state.currentSpeechRate.is_too_fast = true;
    },
    
    clearSpeechRateAlert: (state) => {
      state.currentSpeechRate.is_too_fast = false;
      state.lastSpeechRateAlert = undefined;
    },
    
    addTranscript: (state, action: PayloadAction<{
      text: string;
      speaker: 'customer' | 'salesperson';
      timestamp: string;
      participantId?: string;
      speakerName?: string;
    }>) => {
      // R4: a line of a decided participant takes the decided role and name
      // (it may have left the services before the decision reached them).
      const decision = action.payload.participantId
        ? state.participantDecisions[action.payload.participantId]
        : undefined;
      const newTranscript = decision
        ? {
            ...action.payload,
            speaker: decision.role ?? action.payload.speaker,
            speakerName: decision.name ?? action.payload.speakerName,
          }
        : action.payload;

      // Already restored from the saved session (participant, timestamp, text).
      if (state.transcripts.some((existing) => existing.isRestored && isSameTranscriptLine(existing, newTranscript))) return;

      // Enhanced deduplication: check last 5 transcripts from the same speaker
      const now = new Date(newTranscript.timestamp).getTime();
      const recentTranscripts = state.transcripts.slice(-5); // Check last 5 entries
      
      const isDuplicateRecently = recentTranscripts.some(existing => {
        if (existing.speaker === newTranscript.speaker) {
          const timeDiff = Math.abs(now - new Date(existing.timestamp).getTime());
          // Use enhanced deduplication logic
          return isDuplicateTranscript(existing.text, newTranscript.text, timeDiff);
        }
        return false;
      });
      
      if (!isDuplicateRecently) {
        const id = `${newTranscript.speaker}_${Date.now()}`;
        const transcript: TranscriptEntry = {
          id,
          ...newTranscript,
        };
        state.transcripts.push(transcript);
        
        // Keep only the last TRANSCRIPT_STORE_LIMIT transcripts to prevent memory issues
        if (state.transcripts.length > TRANSCRIPT_STORE_LIMIT) {
          state.transcripts = state.transcripts.slice(-TRANSCRIPT_STORE_LIMIT);
        }
        
        console.log('✅ Added new transcript:', newTranscript.text.slice(0, 50));
      } else {
        // console.log('🔄 Redux: Filtered duplicate transcript:', newTranscript.text.slice(0, 50));
      }
    },
    
    /**
     * History restore: the saved session's lines, minus any the live socket
     * already delivered, merged in by time and kept to the store's limit.
     * Restored lines never flash the transcript bar.
     */
    restoreTranscriptHistory: (state, action: PayloadAction<TranscriptEntry[]>) => {
      const missing = withoutDuplicates(action.payload || [], state.transcripts, isSameTranscriptLine);
      if (missing.length === 0) return;
      state.transcripts = mergeByTime(state.transcripts, missing, (line) => line.timestamp).slice(-TRANSCRIPT_STORE_LIMIT);
    },

    /**
     * History restore: the saved session's cards (objections, FAQs, new
     * inquiries), minus any already here, merged in by time. Restored cards
     * are never `isNew`, so nothing pulses or flashes.
     */
    restoreEntryHistory: (state, action: PayloadAction<ConversationEntry[]>) => {
      const missing = withoutDuplicates(action.payload || [], state.entries, isSameQuestionCard);
      if (missing.length === 0) return;
      state.entries = mergeByTime(state.entries, missing, (entry) => entry.timestamp);
      state.stats.questionsDetected += missing.length;
      state.stats.pendingAnswers += missing.filter((entry) => entry.status === 'pending').length;
    },

    setInterimTranscript: (state, action: PayloadAction<InterimTranscript>) => {
      state.interimTranscript = action.payload;
    },

    clearInterimTranscript: (state) => {
      state.interimTranscript = null;
    },

    clearConversation: (state) => {
      state.entries = [];
      state.transcripts = [];
      state.interimTranscript = null;
      state.stats.questionsDetected = 0;
      state.stats.pendingAnswers = 0;
      // Reset multi-party roster for the next session.
      state.participants = [];
      state.confirmedParticipantIds = [];
      state.salespersonResolved = true;
      state.rosterEditNotice = null;
      state.movedLineKeys = [];
      state.participantDecisions = {};
    },

    /** A rep's edit, saved on the card it was made on (by id, never by text). */
    updateQuestion: (state, action: PayloadAction<{
      id: string;
      editedQuestion: string;
      answer: string;
    }>) => {
      const entry = state.entries.find(e => e.id === action.payload.id);
      if (entry) {
        if (entry.status === 'pending') state.stats.pendingAnswers -= 1;
        entry.question = action.payload.editedQuestion;
        entry.answer = action.payload.answer;
        entry.status = 'completed';
      }
    },
    
    // Utility action for debugging transcript deduplication
    getTranscriptStats: (state) => {
      const stats = {
        totalTranscripts: state.transcripts.length,
        customerTranscripts: state.transcripts.filter(t => t.speaker === 'customer').length,
        salespersonTranscripts: state.transcripts.filter(t => t.speaker === 'salesperson').length,
        recentTranscripts: state.transcripts.slice(-5).map(t => ({
          speaker: t.speaker,
          text: t.text.slice(0, 30) + '...',
          timestamp: t.timestamp
        }))
      };
      console.log('📊 Transcript Stats:', stats);
    },
    
    setIsSalespersonHost: (state, action: PayloadAction<boolean>) => {
      state.isSalespersonHost = action.payload;
    },

    // Upsert the multi-party roster (participants_update, the history
    // restore): a decided participant keeps the decided role and name (R3).
    // Marks the salesperson as resolved when the roster contains one.
    setParticipants: (state, action: PayloadAction<Participant[]>) => {
      mergeRosterEntries(state, action.payload || []);
      if (state.participants.some((p) => p.role === 'salesperson')) {
        state.salespersonResolved = true;
      }
    },

    // Prompt the user to pick the rep from the live roster.
    setSalespersonUnresolved: (state, action: PayloadAction<Participant[]>) => {
      if (action.payload?.length) mergeRosterEntries(state, action.payload);
      state.salespersonResolved = false;
    },

    // Creator confirmed roles + names in the popup. Optimistically applies the
    // assignments to the local roster, records each as this app's decision
    // (`confirmedAt`, this device's clock unless given: a later stale snapshot
    // never reverts it) and marks those participantIds confirmed so the popup
    // does not immediately re-open for them (it re-opens only when a NEW
    // unconfirmed participant joins). Multiple salespeople are allowed —
    // team-selling calls keep every confirmed rep as-is. The popup also
    // dispatches `participantReclassified` per row, which relabels the history.
    confirmParticipants: {
      reducer: (state, action: PayloadAction<{ assignments: ParticipantAssignment[]; confirmedAt: number }>) => {
        const { assignments, confirmedAt } = action.payload;
        const byId = new Map(state.participants.map((p) => [p.participantId, p]));
        for (const a of assignments) {
          if (!a?.participantId) continue;
          const pid = String(a.participantId);
          const prev = byId.get(pid) || { participantId: pid, role: a.role };
          byId.set(pid, { ...prev, role: a.role, name: a.name ?? prev.name, participantId: pid, confirmed: true, confirmedAt });
          // (1) This app's own confirm always applies, flagged unechoed (its stamp is this device's clock).
          const change: ParticipantRoleChange = { participantId: pid, role: a.role, name: a.name, confirmedAt, isLocal: true };
          state.participantDecisions[pid] = decisionAfter(state.participantDecisions[pid], change);
        }
        state.participants = Array.from(byId.values());
        state.salespersonResolved = state.participants.some((p) => p.role === 'salesperson');
        const confirmedIds = new Set(state.confirmedParticipantIds);
        assignments.forEach((a) => a?.participantId && confirmedIds.add(String(a.participantId)));
        state.confirmedParticipantIds = Array.from(confirmedIds);
      },
      prepare: (assignments: ParticipantAssignment[], confirmedAt: number = Date.now()) => ({
        payload: { assignments: assignments || [], confirmedAt },
      }),
    },

    setRosterEditNotice: (state, action: PayloadAction<string | null>) => {
      state.rosterEditNotice = action.payload;
    },

    // Dismissing the popup without changes still acknowledges the current roster
    // so it doesn't immediately re-open (best-guess roles stay in effect).
    acknowledgeParticipants: (state) => {
      const acknowledgedIds = new Set(state.confirmedParticipantIds);
      state.participants.forEach((p) => p?.participantId && acknowledgedIds.add(p.participantId));
      state.confirmedParticipantIds = Array.from(acknowledgedIds);
    },

    // End-of-call "Review participants" saved: mirror the server's rewrite
    // locally (roster row + every transcript of that participant) so the
    // summary that follows counts the corrected roles. Detections carry no
    // participantId here, so they keep their live attribution.
    applyParticipantEdits: (state, action: PayloadAction<ParticipantEditPayload[]>) => {
      const editsById = new Map(
        (action.payload || [])
          .filter((edit) => edit?.participantId)
          .map((edit) => [String(edit.participantId), edit])
      );
      if (editsById.size === 0) return;
      const byId = new Map(state.participants.map((p) => [p.participantId, p]));
      editsById.forEach((edit, pid) => {
        const prev = byId.get(pid) || { participantId: pid, role: edit.role || 'customer' };
        byId.set(pid, { ...prev, role: edit.role || prev.role, name: edit.name ?? prev.name, participantId: pid });
      });
      state.participants = Array.from(byId.values());
      state.transcripts.forEach((transcript) => {
        const edit = transcript.participantId ? editsById.get(String(transcript.participantId)) : undefined;
        if (!edit) return;
        if (edit.role) transcript.speaker = edit.role;
        if (edit.name) transcript.speakerName = edit.name;
      });
      state.salespersonResolved = state.participants.some((p) => p.role === 'salesperson');
    },

    updateEntryFeedback: (state, action: PayloadAction<{ id: string; feedback: EntryFeedback }>) => {
      const entry = state.entries.find(e => e.id === action.payload.id);
      if (entry) {
        entry.feedback = action.payload.feedback;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // A newcomer's mislabelled lines (and the cards they raised) move to them, silently.
      .addCase(participantLinesMoved, (state, action) => {
        const outcome = moveTranscriptLines(state.transcripts, action.payload, state.movedLineKeys);
        state.transcripts = outcome.transcripts;
        state.movedLineKeys = outcome.appliedKeys;
        state.entries = moveQuestionCards(state.entries, action.payload);
      })
      // `from` is `into`: its lines take `into`'s id, name and role; the
      // roster keeps `from` (the server keeps it as an alias) and gains `into`.
      .addCase(participantMerged, (state, action) => {
        const merge = action.payload;
        state.transcripts = mergeTranscriptLines(state.transcripts, merge);
        const target = state.participants.find((p) => p.participantId === merge.intoParticipantId);
        if (target) {
          target.role = merge.intoRole;
          if (merge.intoName) target.name = merge.intoName;
        } else {
          state.participants.push({ participantId: merge.intoParticipantId, role: merge.intoRole, name: merge.intoName });
        }
      })
      // R2: a human decision about one participant (a bot owner's confirm on any
      // device, or a standalone relabel): the roster row, every line, the cards.
      .addCase(participantReclassified, (state, action) => {
        applyRoleChange(state, action.payload);
      })
      .addCase(fetchSpeechRateSettings.fulfilled, (state, action) => {
        if (action.payload) {
          state.speechRateSettings = {
            enabled: action.payload.enabled,
            // `max_words` is the fallback while a backend that predates
            // `wpm_threshold` is still answering; both carry a WPM number.
            wpm_threshold: action.payload.wpm_threshold ?? action.payload.max_words ?? 190,
          };
        }
      })
      .addCase(fetchSpeechRateSettings.rejected, (state, action) => {
        console.error('Failed to fetch speech rate settings:', action.error);
        // Keep default settings on error
      });
  },
});

export const {
  addQuestion,
  addStoredAnswer,
  addAnswer,
  markAnswerError,
  updateCurrentSpeechRate,
  updateSpeechRateAnalysis,
  addSpeechRateAlert,
  clearSpeechRateAlert,
  addTranscript,
  restoreTranscriptHistory,
  restoreEntryHistory,
  setInterimTranscript,
  clearInterimTranscript,
  clearConversation,
  markEntryAsOld,
  updateQuestion,
  getTranscriptStats,
  setIsSalespersonHost,
  setParticipants,
  setSalespersonUnresolved,
  confirmParticipants,
  acknowledgeParticipants,
  setRosterEditNotice,
  applyParticipantEdits,
  updateEntryFeedback,
} = conversationSlice.actions;

export default conversationSlice.reducer;